import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../Button.jsx';
import Chart from '../Chart.jsx';
import InstitutionBadge from '../InstitutionBadge.jsx';
import MaterialIcon from '../../elements/MaterialIcon.jsx';
import MoneyComponent from '../../elements/MoneyComponent.jsx';
import AccountIcon from './AccountIcon.jsx';
import { logoForAccount } from '../../../utilities/accountIcons.js';
import { AccountTransactionList, useAccountTransactions } from './AccountTransactions.jsx';
import { accountRowNotes } from '../../pages/bank/AccountRow.jsx';
import { ruleConditionsText, ruleOutcomeText } from '../../pages/settings/importRuleSummary.js';
import LoanGraphData from '../../../models/LoanGraphData';
import { loadImportRules } from '../../../redux/actions/importRules.js';
import { accountBalanceOn, accountLimitOn } from '../../../utilities/accountSections.js';
import {
	SUMMARY_TRANSACTION_LIMIT,
	axisBounds,
	axisMoney,
	balanceHistory,
	historyIsDrawable,
	importNamesForAccount,
	labelledTicks,
	loadedAccountTransactions,
	loanCategoriesForAccount,
	loanTerms,
	rulesForAccount,
} from '../../../utilities/accountSummary.js';
import { moneyFormatFromCents } from '../../../utilities/moneyFormat.js';
import { isClosedAccount } from '../../../utilities/closedAccounts.js';

/**
 * One account at a glance, opened from its row on the accounts page: its balance, how that has moved,
 * its latest transactions, the import rules that name it and the institution that holds it. A loan
 * also gets its terms, from the budget loan category chosen in its editor, or else the one its
 * payments are filed under. Shown in the same
 * popup as the food page's meal ideas, and read-only: `onEdit` opens the account's editor.
 *
 * Everything comes from what the site already holds. The two exceptions: the import rules are read
 * again on opening, without their match counts, while whatever was loaded before shows (the import
 * page's mappers and merges change them without telling this list), and the account's transactions
 * are asked for when none of the loaded ones are its own, or when more are asked for.
 *
 * `date` is the day balances are read on, as the page's rows read them. `onOpenAccount` switches the
 * summary to another account of the same institution. `onEdit` steps into the account's editor;
 * `onOpenRules` goes to the import rules page.
 */
export default function AccountSummary({ account, institution, date = null, hint = null, onEdit, onClose, onOpenAccount = null, onOpenRules = null }) {
	const dispatch = useDispatch();
	const rules = useSelector((state) => (state.importRules ? state.importRules.rules : null));
	const rulesLoading = useSelector((state) => !!(state.importRules && state.importRules.loading));
	const rulesError = useSelector((state) => (state.importRules ? state.importRules.error : null));
	const accountRules = useSelector((state) => (state.importRules ? state.importRules.accountRules : null));
	const availableCategories = useSelector((state) => (state.importRules ? state.importRules.availableCategories : []));
	const categories = useSelector((state) => state.dashboard.categories);
	const transactionFinder = useSelector((state) => state.dashboard.transactionFinder);
	const transactionFinderUpdates = useSelector((state) => state.dashboard.transactionFinderUpdates);
	const root = useRef(null);

	/**
	 * Keyed by account, so this runs for each account shown: the rules are read again, and a summary
	 * switched to from another starts at its top rather than where that one was scrolled to.
	 */
	useEffect(() => {
		if(!rulesLoading) {
			dispatch(loadImportRules({ counts: false }));
		}
		const content = root.current && root.current.closest('.modal-content');
		if(content) {
			content.scrollTop = 0;
		}
	}, []);

	const balance = accountBalanceOn(account, date);
	const limit = accountLimitOn(account, date);
	const notes = accountRowNotes(account, institution, balance, { showInstitution: false, hint });
	const lastUpdated = account.getLastUpdated();

	const names = useMemo(() => importNamesForAccount(account, accountRules), [account, accountRules]);
	const accountRulesHere = useMemo(() => rulesForAccount(rules, names), [rules, names]);
	const loanCategories = useMemo(() => loanCategoriesForAccount(account, accountRulesHere, categories), [account, accountRulesHere, categories]);
	const isLoan = account.type === 'loan' || loanCategories.length > 0;
	const history = useMemo(() => balanceHistory(account, { loanCategories }), [account, loanCategories]);

	const loaded = useMemo(() => {
		const bank = transactionFinder && transactionFinder.plaidTransactions && typeof transactionFinder.plaidTransactions.toArray === 'function'
			? transactionFinder.plaidTransactions.toArray()
			: [];
		return loadedAccountTransactions(bank, account.id);
	}, [transactionFinder, transactionFinderUpdates, account.id]);

	const owed = balance === null ? null : (account.isLiability() ? -balance : balance);
	const terms = useMemo(() => {
		const asOf = date ? moment.min(date, moment()) : moment();
		return loanCategories.map((category) => loanTerms(category, owed, asOf, new LoanGraphData(category).recentPayment));
	}, [loanCategories, owed, date]);

	const historySection = (
		<section className='meal_idea_panel__section account_summary__history'>
			<h6>{isLoan ? 'Paid down over time' : 'Balance over time'}</h6>
			<BalanceHistoryChart history={history} color={account.color || institution.getColor()} owed={account.isLiability()} />
		</section>
	);

	return (
		<div className='meal_idea_panel account_summary' ref={root}>
			<div className='panel_heading'>
				<AccountIcon icon={account.getIcon()} logo={logoForAccount(account, institution)} color={account.color || institution.getColor()} size={32} />
				<h5 className='meal_idea_details__name account_summary__name' title={account.getDisplayName()}>{account.getDisplayName()}</h5>
				<button type='button' className='btn-flat meal_idea_panel__icon' title='Close' aria-label='Close' onClick={onClose}>
					<MaterialIcon>close</MaterialIcon>
				</button>
			</div>

			<div className='meal_idea_panel__body'>
				<div className='account_summary__hero'>
					<div className='account_summary__balance'>
						{balance === null
							? <span className='account_summary__no_balance'>No balance yet</span>
							: <MoneyComponent amount={balance} showPositive={false} showNegative={false} />}
						{!!limit && <span className='account_summary__limit'>of {moneyFormatFromCents(limit)} limit</span>}
					</div>
					<div className='account_summary__notes'>
						{institution && <span className='account_summary__note institution'>{institution.getName()}</span>}
						{notes.map((note) => (
							<span key={note.key} className={`account_summary__note ${note.key}`} title={note.title}>{note.text}</span>
						))}
						{!!lastUpdated && !notes.some((note) => note.key === 'stale') &&
						<span className='account_summary__note updated' title={lastUpdated.format('LLL')}>As of {lastUpdated.format('MMM D, YYYY')}</span>
						}
					</div>
				</div>

				{historySection}

				{terms.map((loan) => <LoanTerms key={loan.category.id} terms={loan} named={terms.length > 1} />)}

				<section className='meal_idea_panel__section account_summary__transactions'>
					<h6>Recent transactions</h6>
					<SummaryTransactions accountId={account.id} loaded={loaded} />
				</section>

				<section className='meal_idea_panel__section account_summary__rules'>
					<h6>Import rules</h6>
					<AccountRules
						rules={accountRulesHere}
						availableCategories={availableCategories}
						loading={rules === null && !rulesError}
						error={rules === null ? rulesError : null}
						names={names}
						onOpenRules={onOpenRules}
					/>
				</section>

				{institution &&
				<section className='meal_idea_panel__section account_summary__institution'>
					<h6>Institution</h6>
					<InstitutionAccounts account={account} institution={institution} date={date} onOpenAccount={onOpenAccount} />
				</section>
				}
			</div>

			<div className='meal_idea_panel__footer'>
				<div className='meal_idea_panel__actions'>
					<Button flat className='account_summary__edit' onClick={() => onEdit()}>
						<MaterialIcon className='left'>edit</MaterialIcon>
						Edit account
					</Button>
				</div>
			</div>
		</div>
	);
}

/**
 * Chart.js 2's options for a small history: one money axis reaching at least axisBounds(), on round
 * ticks of its own choosing, x labels thinned
 * to labelledTicks() so the last one is always the line's end, and no zooming (it would take the
 * popup's scroll).
 */
export function historyChartOptions(history, owed) {
	const narrow = typeof window !== 'undefined' && window.innerWidth < 600;
	const bounds = axisBounds(history);
	const shown = labelledTicks(history.labels.length, narrow ? 4 : 6);
	const span = bounds ? bounds.span : 0;
	return {
		maintainAspectRatio: false,
		responsive: true,
		animation: { duration: 0 },
		legend: { display: false },
		tooltips: {
			intersect: false,
			mode: 'index',
			filter: (item) => item.yLabel !== null && item.yLabel !== undefined && !Number.isNaN(item.yLabel),
			callbacks: {
				label: (item, data) => {
					const series = data.datasets[item.datasetIndex];
					const prefix = data.datasets.length > 1 ? `${series.label}: ` : (owed ? 'Owed: ' : '');
					return prefix + moneyFormatFromCents(item.yLabel);
				},
			},
		},
		elements: {
			line: { borderWidth: 2, tension: 0 },
			point: { radius: 0, hoverRadius: 4, hitRadius: 8 },
		},
		scales: {
			xAxes: [{
				gridLines: { display: false },
				ticks: {
					autoSkip: false,
					maxRotation: 0,
					fontColor: 'rgba(0, 0, 0, 0.54)',
					callback: (label, index) => (shown.has(index) ? label : null),
				},
			}],
			yAxes: [{
				gridLines: { color: 'rgba(0, 0, 0, 0.06)', drawBorder: false },
				ticks: {
					maxTicksLimit: 5,
					fontColor: 'rgba(0, 0, 0, 0.54)',
					suggestedMin: bounds ? bounds.min : undefined,
					suggestedMax: bounds ? bounds.max : undefined,
					callback: (value) => axisMoney(value, span),
				},
			}],
		},
		plugins: {
			zoom: {
				pan: { enabled: false },
				zoom: { enabled: false },
			},
		},
	};
}

/**
 * A dot for a series with a single point, which has no line to be drawn as, and none otherwise: the
 * line spans gaps, so any other point is joined to something. A loan whose account has one reading
 * beside months of the category's would otherwise not show that reading at all.
 */
export function isolatedPointRadii(values, radius = 4) {
	const lone = values.filter((value) => value !== null).length === 1;
	return values.map((value) => (lone && value !== null ? radius : 0));
}

/** The account's own line in its colour; a loan category's recorded balance beside it, dashed and grey. */
export function historyChartData(history, color) {
	return {
		labels: history.labels,
		datasets: history.series.map((series) => {
			const isAccount = series.key === 'account';
			return {
				label: series.label,
				data: series.values,
				spanGaps: true,
				fill: false,
				borderColor: isAccount ? color : 'rgba(0, 0, 0, 0.38)',
				backgroundColor: isAccount ? color : 'rgba(0, 0, 0, 0.38)',
				borderDash: isAccount ? [] : [5, 4],
				pointBackgroundColor: isAccount ? color : 'rgba(0, 0, 0, 0.38)',
				pointRadius: isolatedPointRadii(series.values),
			};
		}),
	};
}

function BalanceHistoryChart({ history, color, owed }) {
	const data = useMemo(() => historyChartData(history, color), [history, color]);
	const options = useMemo(() => historyChartOptions(history, owed), [history, owed]);
	if(!historyIsDrawable(history)) {
		return (
			<p className='meal_idea_panel__empty'>
				{history.labels.length ? 'Only one balance so far, so nothing to draw yet.' : 'No balances yet.'}
			</p>
		);
	}
	return (
		<React.Fragment>
			<div className='account_summary__chart'>
				<Chart type='line' data={data} options={options} />
			</div>
			{history.series.length > 1 &&
			<ul className='account_summary__legend browser-default'>
				{history.series.map((series, index) => (
					<li key={series.key}>
						<span
							className={`account_summary__swatch ${swatchKind(series)}`}
							style={{ borderColor: data.datasets[index].borderColor, backgroundColor: data.datasets[index].borderColor }}
						/>
						{series.label}
					</li>
				))}
			</ul>
			}
		</React.Fragment>
	);
}

/** How a series is keyed: a dot for one drawn as a lone point, else the line it is drawn as. */
export function swatchKind(series) {
	if(series.values.filter((value) => value !== null).length === 1) {
		return 'is-dot';
	}
	return series.key === 'account' ? 'is-line' : 'is-dashed';
}

/** A loan's terms as the budget records them, and when the balance is paid off at its current payment. */
function LoanTerms({ terms, named = false }) {
	const { category, rate, minimumPayment, payment, months, payoffDate, missing } = terms;
	return (
		<section className='meal_idea_panel__section account_summary__loan'>
			<h6>{named ? `Loan terms: ${category.name}` : 'Loan terms'}</h6>
			<dl className='account_summary__facts'>
				{rate !== null &&
				<div><dt>Rate</dt><dd>{rate}%</dd></div>
				}
				{!!minimumPayment &&
				<div><dt>Minimum</dt><dd>{moneyFormatFromCents(minimumPayment)}/mo</dd></div>
				}
				{!!payment && payment !== minimumPayment &&
				<div><dt>Paying lately</dt><dd>{moneyFormatFromCents(payment)}/mo</dd></div>
				}
				<div>
					<dt>Paid off</dt>
					<dd>
						{payoffText(missing, months, payoffDate)}
						{!!months && <span className='account_summary__fact_detail'>{months} {months === 1 ? 'payment' : 'payments'} to go</span>}
					</dd>
				</div>
			</dl>
		</section>
	);
}

/** When the loan is paid off, or what is missing to say. */
export function payoffText(missing, months, payoffDate) {
	if(missing === 'payment') {
		return 'No payment recorded';
	}
	if(missing === 'rate') {
		return 'No rate recorded';
	}
	if(missing === 'balance') {
		return 'No balance yet';
	}
	if(months === null) {
		return 'Not at this payment';
	}
	return months === 0 ? 'Paid off' : payoffDate.format('MMM YYYY');
}

/**
 * The account's newest transactions from those the dashboard loaded, with the account's whole recent
 * list a tap away. With none loaded, that list is fetched straight off.
 */
function SummaryTransactions({ accountId, loaded }) {
	const [fetchAll, setFetchAll] = useState(false);
	const fetching = fetchAll || !loaded;
	const state = useAccountTransactions(fetching ? accountId : null, SUMMARY_TRANSACTION_LIMIT);
	if(fetching) {
		return <AccountTransactionList {...state} />;
	}
	return (
		<React.Fragment>
			<AccountTransactionList loading={false} error={null} data={loaded} note={loadedNote(loaded)} />
			<button type='button' className='btn-flat account_summary__more' onClick={() => setFetchAll(true)}>
				Show the newest from the bank
			</button>
		</React.Fragment>
	);
}

function loadedNote(loaded) {
	const shown = loaded.transactions.length;
	return loaded.count > shown
		? `The newest ${shown} of the ${loaded.count} the budget has loaded.`
		: `The ${loaded.count === 1 ? 'one' : loaded.count} the budget has loaded.`;
}

/** The rules that name this account, each as the rules page words it, and the way to that page. */
function AccountRules({ rules, availableCategories, loading, error, names, onOpenRules }) {
	let body;
	if(error) {
		body = <p className='meal_idea_panel__empty'>The rules could not be loaded.</p>;
	} else if(loading) {
		body = <p className='meal_idea_panel__empty'>Loading rules…</p>;
	} else if(names.size === 0) {
		body = <p className='meal_idea_panel__empty'>No import maps to this account, so no rule can name it.</p>;
	} else if(!rules.length) {
		body = <p className='meal_idea_panel__empty'>No rule names this account.</p>;
	} else {
		body = (
			<ul className='account_summary__rule_list browser-default'>
				{rules.map((rule) => (
					<li key={rule.id} className='account_summary__rule'>
						<span className='account_summary__rule_position'>{rule.position}</span>
						<span className='account_summary__rule_text'>
							<span className='account_summary__rule_conditions'>{ruleConditionsText(rule)}</span>
							<span className='account_summary__rule_outcome'>→ {ruleOutcomeText(rule, availableCategories)}</span>
						</span>
					</li>
				))}
			</ul>
		);
	}
	return (
		<React.Fragment>
			{body}
			{onOpenRules &&
			<button type='button' className='btn-flat account_summary__more account_summary__open_rules' onClick={onOpenRules}>
				Open import rules
			</button>
			}
		</React.Fragment>
	);
}

/** The institution, and its other accounts, each opening its own summary. */
function InstitutionAccounts({ account, institution, date, onOpenAccount }) {
	const others = (institution.accounts || []).filter((other) => other.id !== account.id && !isClosedAccount(other));
	return (
		<div className='account_summary__institution_block'>
			<div className='account_summary__institution_name'>
				<InstitutionBadge institution={institution.institution} name={institution.getName()} color={institution.getColor()} size={24} />
				<span>{institution.getName()}</span>
			</div>
			{others.length > 0 &&
			<ul className='account_summary__siblings browser-default'>
				{others.map((other) => {
					const otherBalance = accountBalanceOn(other, date);
					return (
						<li key={other.id}>
							<button
								type='button'
								className='btn-flat account_summary__sibling'
								disabled={!onOpenAccount}
								onClick={() => onOpenAccount && onOpenAccount(other, institution)}
							>
								<AccountIcon icon={other.getIcon()} logo={logoForAccount(other, institution)} color={other.color || institution.getColor()} size={20} />
								<span className='account_summary__sibling_name'>
									{other.getDisplayName()}
									{!!other.mask && <span className='account_summary__sibling_mask'> ••{other.mask}</span>}
								</span>
								<span className='account_summary__sibling_amount'>
									{otherBalance === null ? '—' : <MoneyComponent amount={otherBalance} showPositive={false} showNegative={false} />}
								</span>
							</button>
						</li>
					);
				})}
			</ul>
			}
		</div>
	);
}
